'use strict';

import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from '~/shared/src/utilities/metaModel';
import PropertySet from '~/shared/src/mm/PropertySet';
import Item from '~/shared/src/ci/Item';
import Property, {
    PropertyAttributes
} from '~/shared/src/ci/Property';
import EnumProperty, {
    EnumPropertyAttributes
} from '~/shared/src/ci/EnumProperty';


type AllowedPropertyAttributesToGet = PropertyAttributes & Pick<Property,
    'schema'
    | 'propertySet'
    | 'valueLabel'
    | 'attributesModified'
>;
type AllowedPropertyMethodsToGet = Pick<Property,
    'getAttributeOriginalValue'
    | 'isAttributeModified'
    | 'isDeleted'
    | 'isHistorized'
    | 'isModified'
    | 'isNew'
    | 'isValid'
    | 'reset'
    | 'resetAttribute'
>;
type AllowedPropertyAttributesToSet = Partial<Pick<PropertyAttributes,
    'statusId'
    | 'value'
>>;

const allowedPropertyAttributesToGet = keys<AllowedPropertyAttributesToGet>() as string[];
const allowedPropertyMethodsToGet = keys<AllowedPropertyMethodsToGet>() as string[];
const allowedPropertyAttributesToSet = keys<AllowedPropertyAttributesToSet>() as string[];

type AllowedEnumPropertyAttributesToGet = EnumPropertyAttributes & Pick<EnumProperty,
    'schema'
    | 'propertySet'
    | 'isReorderingProperties'
>;
type AllowedEnumPropertyMethodsToGet = Pick<EnumProperty,
    'isModified'
    | 'isNew'
    | 'isValid'
    | 'reset'
    | 'isKeyUnique'
    | 'isOrderNumUnique'
    | 'getUniqueKeys'
    | 'getUniqueValues'
    | 'getMaxOrderNum'
    | 'addProperty'
    | 'addPropertiesFromJSON'
    | 'addPropertyFromJSON'
>;
type AllowedEnumPropertyPropertyAttributesToGet = AllowedPropertyAttributesToGet;
type AllowedEnumPropertyPropertyMethodsToGet = AllowedPropertyMethodsToGet;
type AllowedEnumPropertyPropertyAttributesToSet = Partial<
    Pick<
        PropertyAttributes,
        'statusId'
        | 'key'
        | 'value'
    >
    & { orderNum : number }
>;

const allowedEnumPropertyAttributesToGet = keys<AllowedEnumPropertyAttributesToGet>() as string[];
const allowedEnumPropertyMethodsToGet = keys<AllowedEnumPropertyMethodsToGet>() as string[];
const allowedEnumPropertyPropertyAttributesToGet = keys<AllowedEnumPropertyPropertyAttributesToGet>() as string[];
const allowedEnumPropertyPropertyMethodsToGet = keys<AllowedEnumPropertyPropertyMethodsToGet>() as string[];
const allowedEnumPropertyPropertyAttributesToSet = keys<AllowedEnumPropertyPropertyAttributesToSet>() as string[];

type MapAttributes = typeof Map.prototype;
const mapAttributes = keys<MapAttributes>();

type EnumPropertyPropertyTarget = {
    propertySet : PropertySet;
    keyOrIndex : number | string;
};

class PropertyResolver {
    protected _item : Item;

    constructor( item : Item ) {
        this._item = item;

        return new Proxy( this, this );
    }

    get( target : PropertyResolver, propertyTypeName : string | symbol ) {
        let retVal : any = undefined;

        if ( typeof propertyTypeName === 'string' ) {
            const propertySet = metaModel[ this._item.schema ].getPropertySetForItemTypeAndPropertyType( this._item.itemTypeId, _.upperFirst( propertyTypeName ) );

            if ( propertySet ) {
                // return a proxy to avoid validate get and set
                if ( propertySet.isEnumerated ) {
                    retVal = new Proxy(
                        propertySet,
                        {
                            get: this.getEnumProperty.bind( this )
                        }
                    );
                }
                else {
                    retVal = new Proxy(
                        propertySet,
                        {
                            get: this.getPropertyAttribute.bind( this ),
                            set: this.setPropertyAttribute.bind( this )
                        }
                    );
                }
            }
        }

        return retVal;
    }


    // property
    getPropertyAttribute( propertySet : PropertySet, attribute : symbol ) : ( () => string ) | undefined;
    getPropertyAttribute( propertySet : PropertySet, attribute : string ) : AttributeValue | undefined;
    getPropertyAttribute( propertySet : PropertySet, attribute : string | symbol ) : AttributeValue | ( () => string ) | undefined {
        const property = this._item.properties.get( propertySet.propertyTypeId );

        let retVal : AttributeValue | ( () => string ) | undefined;

        if ( property && !property.isDeleted() ) {
            if ( typeof attribute === 'symbol' ) {
                if ( attribute === Symbol.toPrimitive ) {
                    retVal = () => {
                        return property.value as string;
                    };
                }
            }
            else {
                const attributeToGet = _.lowerFirst( attribute );

                if ( allowedPropertyAttributesToGet.includes( attributeToGet ) ) {
                    retVal = property[ attributeToGet as keyof Omit<typeof Property, 'prototype' | 'uniqueIdCount'> ];
                }
                else if ( allowedPropertyMethodsToGet.includes( attributeToGet ) ) {
                    retVal = ( property[ attributeToGet as keyof Omit<typeof Property, 'prototype' | 'uniqueIdCount'> ] as Function ).bind( property );
                }
                else {
                    throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.get.invalid', { attribute: attribute } );
                }
            }
        }

        return retVal;
    }

    setPropertyAttribute( propertySet : PropertySet, attribute : string, value : AttributeValue ) {
        const attributeToSet = _.lowerFirst( attribute );

        let retVal = false;

        if ( allowedPropertyAttributesToSet.includes( attributeToSet ) ) {
            const propertyExists = this._item.properties.has( propertySet.propertyTypeId ),
                  property       = propertyExists
                      ? this._item.properties.get( propertySet.propertyTypeId )!
                      : this._item.addProperty( propertySet.propertyTypeId );

            try {
                if ( attributeToSet === 'statusId' && typeof value === 'number' ) {
                    property.statusId = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'value' && propertySet.isValueSettable ) {
                    if ( property.isDeleted() ) {
                        property.statusId = ( property.parent as Item ).statusId;
                    }

                    property.value = value;
                    retVal = true;
                }
            }
            catch ( e ) {
                // delete just created property if set is invalid
                if ( !propertyExists ) {
                    this._item.properties.delete( propertySet.propertyTypeId );
                }

                throw e;
            }
        }
        else {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.set.invalid', { attribute: attribute } );
        }

        return retVal;
    }


    // enumerated property
    getEnumProperty( propertySet : PropertySet, attribute : string ) {
        const attributeToGet = _.lowerFirst( attribute );

        let retVal : any = undefined;

        // proxy for getting the properties of the enumerated property
        if ( attributeToGet === 'properties' ) {
            retVal = new Proxy(
                propertySet,
                {
                    get: this.getEnumPropertyProperty.bind( this )
                }
            )
        }

        // get the attribute or method if exists
        else {
            let enumProperty = this._item.enumProperties.get( propertySet.propertyTypeId );

            const enumPropertyExists = !!enumProperty;

            if (
                !enumPropertyExists
                && (
                    attributeToGet === 'addProperty'
                    || attributeToGet === 'addPropertiesFromJSON'
                    || attributeToGet === 'addPropertyFromJSON'
                )
            ) {
                enumProperty = this._item.addEnumProperty( propertySet.propertyTypeId );
            }

            if ( enumProperty ) {
                try {
                    if ( allowedEnumPropertyAttributesToGet.includes( attributeToGet ) ) {
                        retVal = enumProperty[ attributeToGet as keyof Omit<typeof EnumProperty, 'prototype'> ];
                    }
                    else if ( allowedEnumPropertyMethodsToGet.includes( attributeToGet ) ) {
                        retVal = ( enumProperty[ attributeToGet as keyof Omit<typeof EnumProperty, 'prototype'> ] as Function ).bind( enumProperty );
                    }
                    else {
                        throw new LocalizableError( 'YN.base.dataObject.enumProperty.attributes.validation.get.invalid', { attribute: attribute } );
                    }
                }
                catch ( e ) {
                    // delete just created property if set is invalid
                    if ( !enumPropertyExists ) {
                        this._item.enumProperties.delete( propertySet.propertyTypeId );
                    }

                    throw e;
                }
            }
        }

        return retVal;
    }

    // enumerated property's property
    getEnumPropertyProperty( propertySet : PropertySet, keyOrIndex : symbol ) : undefined;
    getEnumPropertyProperty( propertySet : PropertySet, keyOrIndex : string ) : any;
    getEnumPropertyProperty( propertySet : PropertySet, keyOrIndex : string | symbol ) {
        const enumProperty = this._item.enumProperties.get( propertySet.propertyTypeId );

        let retVal : any = undefined;

        if ( mapAttributes.includes( keyOrIndex as keyof MapAttributes ) ) {
            if ( enumProperty ) {
                retVal = enumProperty.properties[ keyOrIndex as keyof MapAttributes ];

                if ( typeof retVal === 'function' ) {
                    retVal = retVal.bind( enumProperty.properties );
                }
            }
        }
        else if ( typeof keyOrIndex !== 'symbol' ) {
            let convertedKeyOrIndex : number | string = keyOrIndex;

            if ( String( Number( keyOrIndex ) ) === keyOrIndex ) {
                convertedKeyOrIndex = Number( keyOrIndex );
            }

            retVal = new Proxy(
                {
                    propertySet,
                    keyOrIndex: convertedKeyOrIndex
                },
                {
                    get: this.getEnumPropertyPropertyAttribute.bind( this ),
                    set: this.setEnumPropertyPropertyAttribute.bind( this )
                }
            );
        }

        return retVal;
    }

    getEnumPropertyPropertyAttribute( target : EnumPropertyPropertyTarget, attribute : symbol ) : ( () => string ) | undefined;
    getEnumPropertyPropertyAttribute( target : EnumPropertyPropertyTarget, attribute : string ) : AttributeValue | undefined;
    getEnumPropertyPropertyAttribute( target : EnumPropertyPropertyTarget, attribute : string | symbol ) : AttributeValue | ( () => string ) | undefined {
        const propertySet  = target.propertySet,
              keyOrIndex   = target.keyOrIndex,
              enumProperty = this._item.enumProperties.get( propertySet.propertyTypeId );

        let retVal : AttributeValue | ( () => string ) | undefined;

        if ( enumProperty ) {
            const property = this.getPropertyFromEnumProperty( enumProperty, keyOrIndex );

            if ( property && !property.isDeleted() ) {
                if ( typeof attribute === 'symbol' ) {
                    if ( attribute === Symbol.toPrimitive ) {
                        retVal = () => {
                            return property.value as string;
                        };
                    }
                }
                else {
                    const attributeToGet = _.lowerFirst( attribute );

                    if ( allowedEnumPropertyPropertyAttributesToGet.includes( attributeToGet ) ) {
                        retVal = property[ attributeToGet as keyof Omit<typeof Property, 'prototype' | 'uniqueIdCount'> ];
                    }
                    else if ( allowedEnumPropertyPropertyMethodsToGet.includes( attributeToGet ) ) {
                        retVal = ( property[ attributeToGet as keyof Omit<typeof Property, 'prototype' | 'uniqueIdCount'> ] as Function ).bind( property );
                    }
                    else {
                        throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.get.invalid', { attribute: attribute } );
                    }
                }
            }
        }

        return retVal;
    }

    setEnumPropertyPropertyAttribute( target : EnumPropertyPropertyTarget, attribute : string, value : AttributeValue ) {
        const propertySet    = target.propertySet,
              keyOrIndex     = target.keyOrIndex,
              attributeToSet = _.lowerFirst( attribute );

        let retVal = false;

        if ( allowedEnumPropertyPropertyAttributesToSet.includes( attributeToSet ) ) {
            const enumPropertyExists = this._item.enumProperties.has( propertySet.propertyTypeId ),
                  enumProperty       = enumPropertyExists
                      ? this._item.enumProperties.get( propertySet.propertyTypeId )!
                      : this._item.addEnumProperty( propertySet.propertyTypeId ),
                  existingProperty   = this.getPropertyFromEnumProperty( enumProperty, keyOrIndex ),
                  propertyExists     = !!existingProperty,
                  property           = existingProperty
                      ? existingProperty
                      : enumProperty.addProperty( keyOrIndex );

            try {
                if ( attributeToSet === 'statusId' && typeof value === 'number' ) {
                    property.statusId = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'orderNum' && typeof value === 'number' && ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) ) {
                    if ( property.isDeleted() ) {
                        property.statusId = ( property.parent as EnumProperty ).parent.statusId;
                    }

                    property.orderNum = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'key' && propertySet.isKeySettable ) {
                    if ( property.isDeleted() ) {
                        property.statusId = ( property.parent as EnumProperty ).parent.statusId;
                    }

                    property.key = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'value' && propertySet.isValueSettable ) {
                    if ( property.isDeleted() ) {
                        property.statusId = ( property.parent as EnumProperty ).parent.statusId;
                    }

                    property.value = value;
                    retVal = true;
                }
            }
            catch ( e ) {
                // delete just created property if set is invalid
                if ( !enumPropertyExists ) {
                    this._item.enumProperties.delete( propertySet.propertyTypeId );
                }
                else if ( !propertyExists ) {
                    enumProperty.properties.delete( property.stringKey );
                }

                throw e;
            }
        }
        else {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.set.invalid', { attribute: attribute } );
        }

        return retVal;
    }

    getPropertyFromEnumProperty( enumProperty : EnumProperty, keyOrIndex : number | string ) : Property | undefined {
        let retVal : Property | undefined = undefined;

        // get property by index (key sorted asc)
        if ( typeof keyOrIndex === 'number' ) {
            const properties : Property[] = [ ...enumProperty.properties.values() ];

            properties.sort( ( property1, property2 ) => {
                if ( property1.key && property2.key ) {
                    if ( property1.key > property2.key ) {
                        return 1;
                    }
                    else if ( property1.key < property2.key ) {
                        return -1;
                    }
                }

                return 0;
            } );

            retVal = properties[ keyOrIndex ];
        }

        // get property by stringKey
        else if ( enumProperty.properties.has( keyOrIndex ) ) {
            retVal = enumProperty.properties.get( keyOrIndex );
        }

        // get property by key (only the first)
        else {
            const properties : Property[] = [ ...enumProperty.properties.values() ];

            for ( let i = 0; i < properties.length; i++ ) {
                if ( properties[ i ].key === keyOrIndex ) {
                    retVal = properties[ i ];
                    break;
                }
            }
        }

        return retVal;
    }
}

export default PropertyResolver;
